/**
 * 由 EventDetailDto + taskList 组装时间轴节点（按时间排序）
 */

function toMs(val) {
  if (val == null || val === '') return null;
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? null : t;
}

function fmt(val) {
  if (val == null || val === '') return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  const p = (n) => (n < 10 ? '0' + n : '' + n);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

const EVENT_SOURCE = { 1: '自动上报', 2: '人工上报' };
const EVENT_STATUS = {
  1: '已提交',
  2: '已上报',
  3: '已启动应急响应',
  4: '已结束',
  5: '手动结束',
  6: '误报'
};
const URGENCY = { 1: '特别重大', 2: '重大', 3: '较大', 4: '一般' };
const EVENT_TYPE = { 1: '自然灾害', 2: '事故灾难', 3: '公共卫生', 4: '社会安全' };
const RESPONSE_LEVEL = { 1: '一级响应', 2: '二级响应', 3: '三级响应' };
const ASSIGN_TYPE = { 1: '工作组派工', 2: '专家派工' };
const ASSIGN_STATUS = { 1: '待接单', 2: '处理中', 3: '已完成', 4: '已超时' };

export function buildTimeline(detail) {
  if (!detail) return [];
  const raw = [];

  const push = (timeVal, dot, title, body, meta) => {
    const ts = toMs(timeVal);
    if (ts == null) return;
    raw.push({
      ts,
      timeStr: fmt(timeVal),
      dot,
      title,
      body: body || '—',
      meta: meta || ''
    });
  };

  if (detail.occurTime) {
    const src = EVENT_SOURCE[detail.eventSource] || '';
    push(
      detail.occurTime,
      'event',
      '事件发生',
      detail.eventDescription || '—',
      [src, detail.location ? `位置：${detail.location}` : ''].filter(Boolean).join(' · ')
    );
  }

  if (detail.createTime) {
    const oc = toMs(detail.occurTime);
    const cr = toMs(detail.createTime);
    if (cr !== oc) {
      push(detail.createTime, 'state', '事件记录已创建', `事件编号 ${detail.eventNumber || '—'}`, `当前状态：${EVENT_STATUS[detail.eventStatus] || detail.eventStatus}`);
    }
  }

  if (detail.reportTime) {
    push(
      detail.reportTime,
      'event',
      '事件上报',
      `上报人：${detail.reporterName || '—'}`,
      `事件状态：${EVENT_STATUS[detail.eventStatus] || detail.eventStatus}`
    );
  }

  if (detail.verifyInfo) {
    const t = detail.updateTime || detail.reportTime || detail.createTime;
    push(t, 'state', '指挥核实', detail.verifyInfo, 'verifyInfo');
  }

  if (detail.planDetail && (detail.planId || detail.planDetail.id)) {
    const p = detail.planDetail;
    const t = detail.reportTime || detail.createTime || detail.occurTime;
    const ver = p.planVersion != null ? `V${p.planVersion}` : '';
    push(
      t,
      'plan',
      '关联应急预案',
      p.planName || '—',
      [p.planCode, ver].filter(Boolean).join(' · ')
    );
  }

  if (detail.eventStatus >= 3 && detail.eventStatus !== 6) {
    const tasks = detail.taskList || [];
    let t = null;
    if (tasks.length) {
      const times = tasks
        .map((x) => toMs(x.assignTime) || toMs(x.createTime))
        .filter((x) => x != null)
        .sort((a, b) => a - b);
      if (times.length) t = times[0];
    }
    const timeVal =
      t != null
        ? new Date(t)
        : detail.updateTime || detail.reportTime || detail.createTime;
    push(
      timeVal,
      'state',
      '启动应急响应',
      `响应等级：${RESPONSE_LEVEL[detail.responseLevel] || detail.responseLevel || '—'}`,
      `事件状态：${EVENT_STATUS[detail.eventStatus] || detail.eventStatus}`
    );
  }

  (detail.taskList || []).forEach((task) => {
    const t0 = task.assignTime || task.createTime;
    const typeLabel = ASSIGN_TYPE[task.assignType] || '';
    const group = task.groupName || '—';
    push(
      t0,
      'task',
      `应急派工：${group}`,
      task.workDescription || task.groupDuties || '—',
      [typeLabel, `状态：${ASSIGN_STATUS[task.assignStatus] || task.assignStatus}`].filter(Boolean).join(' · ')
    );
    if (task.assignStatus === 3 && task.endTime) {
      push(
        task.endTime,
        'task',
        `工单完成：${group}`,
        task.disposalContent || task.feedbackInfo || '已完成',
        ASSIGN_TYPE[task.assignType] || ''
      );
    }
    if (task.assignStatus === 4) {
      const tu = task.updateTime || task.endTime || task.assignTime;
      push(tu, 'task', `工单超时：${group}`, task.feedbackInfo || '请关注处置进度', task.timeoutMinutes != null ? `超时时长(分钟)：${task.timeoutMinutes}` : '');
    }
  });

  if ([4, 5, 6].includes(detail.eventStatus)) {
    push(
      detail.updateTime || detail.reportTime,
      'end',
      EVENT_STATUS[detail.eventStatus] || '事件结束',
      detail.eventStatus === 6 ? '本事件标记为误报' : '事件已闭环或结束',
      `eventStatus=${detail.eventStatus}`
    );
  }

  raw.sort((a, b) => a.ts - b.ts);

  let last = null;
  return raw.filter((item) => {
    if (last != null && item.ts === last.ts && item.title === last.title) return false;
    last = item;
    return true;
  });
}

export function summaryFromDetail(detail) {
  if (!detail) return {};
  return {
    eventNumber: detail.eventNumber,
    eventName: detail.eventName,
    eventTypeLabel: EVENT_TYPE[detail.eventType] || detail.eventType,
    urgencyLabel: URGENCY[detail.urgencyLevel] || detail.urgencyLevel,
    eventStatusLabel: EVENT_STATUS[detail.eventStatus] || detail.eventStatus,
    occurTime: fmt(detail.occurTime),
    reportTime: fmt(detail.reportTime),
    location: detail.location,
    planName: (detail.planDetail && detail.planDetail.planName) || '—',
    responseLevelLabel: RESPONSE_LEVEL[detail.responseLevel] || detail.responseLevel,
    reporterName: detail.reporterName
  };
}
